<template>
  <div class="app-shell">
    <Sidebar
      v-if="isAuthenticated"
      :nav-open="navOpen"
      :collapsed="sidebarCollapsed"
      @update:collapsed="sidebarCollapsed = $event"
      @close="navOpen = false"
    />

    <!-- Mobile / tablet drawer backdrop -->
    <transition name="backdrop">
      <div
        v-if="isAuthenticated && navOpen"
        class="nav-backdrop"
        @click="navOpen = false"
      ></div>
    </transition>

    <div
      class="main-content"
      :class="{ 'has-progress-bar': isAuthenticated, 'progress-collapsed': progressCollapsed }"
    >
      <TopBar
        v-if="isAuthenticated"
        :current-page="currentPage"
        @toggle-nav="navOpen = !navOpen"
      />
      <div class="page-content">
        <router-view />
      </div>
      <Footer />
    </div>

    <ProjectProgressSidebar v-if="isAuthenticated" v-model:collapsed="progressCollapsed" />
  </div>

  <!-- Global Toast Notification -->
  <ToastNotification ref="toastRef" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from './stores/authStore'
import usageTracker from './services/usageTracker'
import Sidebar from './components/Sidebar.vue'
import TopBar from './components/TopBar.vue'
import ProjectProgressSidebar from './components/ProjectProgressSidebar.vue'
import ToastNotification from './components/ToastNotification.vue'
import Footer from './components/Footer.vue'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const toastRef = ref(null)

const progressCollapsed = ref(false)
const sidebarCollapsed = ref(false)
const navOpen = ref(false)

// Provide toast to all components
provide('toast', {
  show: (message, type = 'success', title = null) => {
    toastRef.value?.show(message, type, title)
  }
})

const isAuthenticated = computed(() => authStore.isAuthenticated)

const currentPage = computed(() => {
  const map = {
    Home:         'Tableau de Bord',
    Users:        'Utilisateurs',
    TrustPassport:'Passeport de Confiance',
    Activities:   'Historique Activités',
    Settings:     'Configuration & Pilotage',
    ProjectDetail:'Détail Projet',
  }
  return map[route.name] || route.name
})

// Close the mobile drawer whenever the route changes
watch(() => route.fullPath, () => {
  navOpen.value = false
  // Track page view
  usageTracker.trackPageView(route.path, route.name)
})

// Reset drawer state when resizing up to desktop so it can't get stuck
let mq
const handleMq = (e) => { if (!e.matches) navOpen.value = false }

// Track session end when user logs out
watch(() => authStore.isAuthenticated, (newVal, oldVal) => {
  if (oldVal && !newVal) {
    // User logged out - send usage log
    usageTracker.endSession(authStore.userEmail, authStore.userHashId)
  } else if (!oldVal && newVal) {
    // User logged in - start new session
    usageTracker.startSession()
    usageTracker.trackPageView(route.path, route.name)
  }
})

onMounted(async () => {
  mq = window.matchMedia('(max-width: 1024px)')
  mq.addEventListener('change', handleMq)

  if (!isAuthenticated.value) {
    try {
      await authStore.getCurrentUser()
      await authStore.getProfile()
    } catch (_) {
      // If not authenticated, just keep isAuthenticated as false
    }
  } else {
    // Track initial page view if already authenticated
    usageTracker.startSession()
    usageTracker.trackPageView(route.path, route.name)
  }

  // Track when user leaves the page
  window.addEventListener('beforeunload', () => {
    if (authStore.isAuthenticated) {
      usageTracker.endSession(authStore.userEmail, authStore.userHashId)
    }
  })
})

onUnmounted(() => {
  if (mq) mq.removeEventListener('change', handleMq)
  // Send final usage log when component unmounts
  if (authStore.isAuthenticated) {
    usageTracker.endSession(authStore.userEmail, authStore.userHashId)
  }
})
</script>

<style>
/* ─── Global app shell ─── */
.app-shell {
  display: flex;
  min-height: 100vh;
  background: var(--color-bg-page);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  transition: margin-right 0.25s cubic-bezier(.4, 0, .2, 1);
}

/* Right progress sidebar reserves space on desktop only */
.main-content.has-progress-bar {
  margin-right: 280px;
}
.main-content.has-progress-bar.progress-collapsed {
  margin-right: 56px;
}

.page-content {
  flex: 1;
  padding: 2rem 2.5rem;
  overflow-y: auto;
  container-type: inline-size;
  container-name: page;
}

/* ─── Mobile / tablet drawer backdrop ─── */
.nav-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 45;
  display: none;
}
.backdrop-enter-active,
.backdrop-leave-active { transition: opacity 0.25s ease; }
.backdrop-enter-from,
.backdrop-leave-to { opacity: 0; }

/* ─── The right progress rail only docks on large screens (≥1280px); below
   that it is hidden, so the central area reclaims its reserved space. ─── */
@media (max-width: 1279px) {
  .main-content.has-progress-bar,
  .main-content.has-progress-bar.progress-collapsed {
    margin-right: 0;
  }
}

/* ─── Tablet & below (≤1024px): the left nav becomes an off-canvas drawer ─── */
@media (max-width: 1024px) {
  .page-content {
    padding: 1.75rem 2rem;
  }
  .nav-backdrop { display: block; }
}

/* ─── Mobile (≤640px) ─── */
@media (max-width: 640px) {
  .page-content {
    padding: 1.25rem 1rem;
  }
}
</style>
